import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { CRISIS, SITE } from "../content/site";

const NAV = [
  { name: "Home", path: "/" },
  { name: "About TJ", path: "/about" },
  { name: "Get Support", path: "/support" },
  { name: "Walking for Hope", path: "/walking-for-hope" },
  { name: "Get Involved", path: "/get-involved" },
];

const linkClass = ({ isActive }) =>
  `rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-sage-soft text-sage" : "text-body hover:bg-sage-soft/60 hover:text-sage"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      {/* Crisis strip — static, never overlays content */}
      <div
        className="border-b border-gold/30 bg-sage px-4 py-2.5 text-cream sm:px-8"
        data-testid="crisis-strip"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center text-xs sm:justify-between sm:text-sm">
          <p>
            If you or someone you know is struggling, help is available — free, 24/7.
          </p>
          <div className="flex items-center gap-3 font-semibold">
            <a
              href={CRISIS.callHref}
              className="underline decoration-gold decoration-2 underline-offset-4 hover:text-gold-soft"
              data-testid="crisis-strip-call-link"
            >
              Call or text 988
            </a>
            <a
              href={CRISIS.chatHref}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-gold/60 decoration-2 underline-offset-4 hover:text-gold-soft"
              data-testid="crisis-strip-chat-link"
            >
              Chat
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-sage/10 bg-cream/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-8 lg:px-16">
          <Link
            to="/"
            className="flex items-center rounded-md"
            aria-label={`${SITE.name} — home`}
            data-testid="nav-logo-link"
          >
            <Logo className="h-9 w-auto sm:h-12" />
          </Link>

          <nav aria-label="Main navigation" className="hidden lg:block">
            <ul className="flex items-center gap-1">
              {NAV.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    className={linkClass}
                    data-testid={`nav-link-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {item.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/get-involved#donate"
              className="hidden min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-sage transition-colors hover:bg-sage-soft sm:inline-flex"
              data-testid="nav-donate-link"
            >
              <Heart className="h-4 w-4" aria-hidden="true" /> Donate
            </Link>
            <Link
              to="/support"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-sage px-4 py-2 text-sm font-semibold text-cream transition-colors hover:bg-sage-light"
              data-testid="nav-get-help-button"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> Get Help Now
            </Link>
            <button
              type="button"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-sage hover:bg-sage-soft lg:hidden"
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {open && (
          <nav
            id="mobile-menu"
            aria-label="Mobile navigation"
            className="border-t border-sage/10 bg-cream px-4 pb-6 pt-2 lg:hidden"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-1">
              {NAV.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-lg px-4 py-3 text-base font-medium ${
                        isActive
                          ? "bg-sage-soft text-sage"
                          : "text-body hover:bg-sage-soft/60"
                      } ${location.pathname === item.path ? "" : ""}`
                    }
                    data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {item.name}
                  </NavLink>
                </li>
              ))}
              <li>
                <Link
                  to="/get-involved#donate"
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-4 py-3 text-base font-semibold text-gold-dark hover:bg-gold-soft/50"
                  data-testid="mobile-nav-donate-link"
                >
                  Donate
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </header>
    </>
  );
};
